import _extends from "@babel/runtime/helpers/esm/extends";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useMemo } from 'react';
import { Components as PaginationComponents } from './pagination.style';
import Icon from '../Icon';
import { usePagination, DOTS } from './usePagination';
import Select from '../Select';
const defaultPageSizeOptions = [10, 20, 30, 50];
const getPageSizeOptions = pageSizeOptions => {
  return pageSizeOptions.map(pageSize => ({
    label: "".concat(pageSize, "/per page"),
    value: pageSize.toString()
  }));
};
function Pagination(_ref) {
  let {
    className = 'pagination',
    totalItems,
    hiddenLabels = false,
    previousLabel = 'Previous',
    nextLabel = 'Next',
    pageSize: pageSizeNum,
    pageSizeOptions = defaultPageSizeOptions,
    currentPage,
    siblingCount = 1,
    fit = 'medium',
    hidePageSizeOptions = true,
    hidePageNumber = false,
    onPageChange,
    onPageSizeOptionsChange,
    disabledTruncation = false
  } = _ref;
  const [pageSize, setPageSize] = useState(() => {
    if (hidePageSizeOptions) {
      return (pageSizeNum === null || pageSizeNum === void 0 ? void 0 : pageSizeNum.toString()) || '';
    }
    const pageSizeNumber = pageSizeOptions.find(value => Number(value) === Number(pageSizeNum));
    if (pageSizeNumber) {
      return pageSizeNumber.toString();
    }
    return pageSizeOptions[0].toString();
  });
  const paginationRange = usePagination({
    currentPage,
    pageSize,
    totalItems,
    siblingCount,
    disabledTruncation
  });
  const pageCount = paginationRange ? paginationRange[paginationRange.length - 1] : 0;
  /**
   * @description
   * Emits the Previous Page Number
   */
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };
  /**
   * @description
   * Emits the Next Page Number
   */
  const handleNextPage = () => {
    if (currentPage < pageCount) {
      onPageChange(currentPage + 1);
    }
  };
  /**
   * @description
   * Emits the Current Page Number
   */
  const handlePageSelected = index => {
    const current = index;
    if (currentPage === current) return;
    onPageChange(current);
  };
  const pageSizeSelectOptions = useMemo(() => getPageSizeOptions(pageSizeOptions), [pageSizeOptions]);
  return _jsxs(PaginationComponents.ParentWrapper, _extends({
    className: className
  }, {
    children: [_jsx(PaginationComponents.Wrapper, {
      children: _jsxs(PaginationComponents.List, {
        children: [_jsx(PaginationComponents.Item, {
          children: _jsxs(PaginationComponents.Link, _extends({
            fit: fit,
            variant: currentPage - 1 === 0 ? 'disabled' : 'default',
            onClick: () => {
              handlePreviousPage();
            }
          }, {
            children: [_jsx(Icon, {
              name: "chevron-left"
            }), !hiddenLabels && previousLabel]
          }))
        }), (paginationRange || []).map((pageNumber, index) => {
          if (pageNumber === DOTS && !hidePageNumber) {
            return _jsx(PaginationComponents.Item, {
              children: _jsx(PaginationComponents.Link, _extends({
                fit: fit,
                variant: "disabled"
              }, {
                children: DOTS
              }))
            }, "dots-".concat(index));
          }
          if (!hidePageNumber) {
            return _jsx(PaginationComponents.Item, _extends({
              className: pageNumber === currentPage ? 'active' : 'default'
            }, {
              children: _jsx(PaginationComponents.Link, _extends({
                fit: fit,
                variant: pageNumber === currentPage ? 'active' : 'default',
                onClick: () => {
                  handlePageSelected(pageNumber);
                }
              }, {
                children: pageNumber
              }))
            }), pageNumber);
          }
          return _jsx(_Fragment, {});
        }), _jsx(PaginationComponents.Item, {
          children: _jsxs(PaginationComponents.Link, _extends({
            fit: fit,
            variant: currentPage - 1 === pageCount - 1 ? 'disabled' : 'default',
            onClick: () => {
              handleNextPage();
            }
          }, {
            children: [!hiddenLabels && nextLabel, _jsx(Icon, {
              name: "chevron-right"
            })]
          }))
        })]
      })
    }), !hidePageSizeOptions && !!pageSizeSelectOptions.length && _jsx(PaginationComponents.Select, {
      children: _jsx(Select, {
        options: pageSizeSelectOptions,
        name: 'pageSize',
        id: 'pageSize',
        value: [pageSize],
        fit: fit,
        onChange: e => {
          onPageSizeOptionsChange === null || onPageSizeOptionsChange === void 0 ? void 0 : onPageSizeOptionsChange(+e.target.value);
          setPageSize(e.target.value);
          onPageChange(1);
        },
        variant: 'default'
      })
    })]
  }));
}
export default Pagination;